import { LONG_PRESS_DELAY, SUSTAIN_TIME, getPosition } from './position';
import { PROP_MAP, type TooltipProps } from './interface';
import { useCallback, useRef, useState } from 'react';

import { Devices } from '../../service/Devices';
import { getPlatform } from '../../utils';
import { modifyProps } from '../../generators/modifyProps';
import { useToggle } from '../../hooks/useToggle';
import { useScrollClose } from '../../hooks/useScrollClose';

export const withLogic =
  (Component: React.ComponentType<TooltipProps>): React.FC<TooltipProps> =>
  (userProps: TooltipProps) => {
    const props = modifyProps(userProps, PROP_MAP);
    const { children, ...rest } = props;
    const ref = useRef<HTMLDivElement>(null);
    const { toggle, selected, close } = useToggle(props.open);
    const [element, setElement] = useState<HTMLDivElement | null>(null);

    const triggerRef = useCallback((element: HTMLDivElement) => {
      if (element) {
        setElement(element);
      }
    }, []);

    const { desktop } = Devices.useMedia();

    const { offsetX, offsetY, newPosition } = getPosition(
      ref.current,
      element,
      rest.position,
      props.triggerSpacing,
      desktop
    );

    const timerRef = useRef<NodeJS.Timeout>();
    const isTouchActive = useRef(false);
    useScrollClose(close);
    return (
      <>
        <Component
          {...{
            ...rest,
            offsetX,
            offsetY,
            _platform: getPlatform(),
            width: '300px',
            position: newPosition || props.position,
            open: Boolean(selected),
            triggerRef
          }}
        />
        <div
          onMouseEnter={() => {
            if (!(window && 'ontouchstart' in window)) {
              toggle();
            }
          }}
          onMouseLeave={close}
          onTouchStart={() => {
            if (window && 'ontouchstart' in window && Devices.isMobile()) {
              isTouchActive.current = true;
              timerRef.current = setTimeout(toggle, LONG_PRESS_DELAY);
            }
          }}
          onTouchEnd={() => {
            if (Devices.isMobile()) {
              isTouchActive.current = false;
              if (selected) {
                setTimeout(close, SUSTAIN_TIME);
              } else {
                if (timerRef.current) {
                  clearTimeout(timerRef.current);
                }
              }
            }
          }}
          ref={ref}
          style={{ width: 'fit-content' }}
        >
          {children}
        </div>
      </>
    );
  };

export { TooltipProps };
